import * as app from 'durandal/app';
import * as router from 'plugins/router';
import * as ko from 'knockout';
import * as segmentAnalytics from 'segmentAnalytics';

import { NavbarTop, NavbarTopProps } from '@/features/navigation';
import { isReferralsAvailableForCurrentCompany } from '@/features/referrals';
import contactsStore, {
  StoreActionType as ContactsStoreActionType,
} from '@/legacy/features/companies/payees/contactsFragment/ContactsFragment.store';
import payablesStore, {
  StoreActionType as PayablesStoreActionType,
} from '@/legacy/features/companies/payments/components/PayablesPage.store';
import SingleSignOnFeatureToggleService from '@/legacy/features/companies/singleSignOn/services/singleSignOnFeatureToggleService';
import receivablesStore, {
  StoreActionType as ReceivablesStoreActionType,
} from '@/legacy/features/user/requests/components/ReceivablesPage.store';
import * as companyService from '@/legacy/services/company';
import CompanyEventService from '@/legacy/services/companyEventService';
import * as emailPaymentRequestsService from '@/legacy/services/emailPaymentRequests';
import * as userService from '@/legacy/services/user';

import { EventName, FormEventType, SignoutContext } from '../../../features/analytics/helper';

type CurrentTab =
  | 'payments'
  | 'paymentRequests'
  | 'payees'
  | 'bank'
  | 'approvalRules'
  | 'documents'
  | 'dashboard';

export class ViewModel {
  private userService: userService.UserService = userService.Instance;

  private companyService: companyService.CompanyService = companyService.Instance;

  private routerNavigationSubscription: DurandalEventSubscription;

  private paymentRequestEmailKeyUpdateSubscription: DurandalEventSubscription;

  private currentTab = ko.observable<CurrentTab>(null);

  private PermissionId: typeof companyService.PermissionId;

  // Payment request
  private myInvoicesUrl = ko.observable<string>(null);

  private myInvoicesCount = ko.observable<number>(0);

  private paymentRequestEmailKey = ko.observable<string>(null);

  private showReferralsNavItem = companyService.Instance.CompanyId.mapAsync(
    isReferralsAvailableForCurrentCompany
  );

  /** Links to the first settings panel that they have access to. */
  private settingsHref = ko.pureComputed({
    read: () => {
      if (this.companyService.HasPermissionTo(this.PermissionId.COMPANY_EDIT)) {
        return '#user/settings/company';
      }
      if (
        this.isAccountingFirm() &&
        this.companyService.HasPermissionTo(this.PermissionId.COMPANY_EDIT)
      ) {
        return '#user/settings/accountingFirm';
      }
      if (this.companyService.HasPermissionTo(this.PermissionId.USER_ADD_EDIT)) {
        return '#user/settings/users';
      }
      if (this.isSingleSignOnVisible()) {
        return '#user/settings/sso';
      }

      return '#user/settings/controls';
    },
    deferEvaluation: true,
  });

  public react = {
    NavbarTop,
  };

  constructor() {
    this.PermissionId = companyService.PermissionId;

    // If the email key changes reload the unpaid invoices
    this.paymentRequestEmailKey.subscribe(() => {
      this.reloadUnpaidInvoices();
    });
  }

  public attached() {
    this.routerNavigationSubscription = app.on('router:navigation:complete').then(() => {
      this.updateCurrentTab();
    });
    this.updateCurrentTab();

    this.paymentRequestEmailKeyUpdateSubscription = app
      .on('paymentRequest:refresh')
      .then((koEmailPaymentRequest: emailPaymentRequestsService.KoEmailPaymentRequest) => {
        this.paymentRequestEmailKey(koEmailPaymentRequest.emailKey());

        this.reloadUnpaidInvoices();
      });
  }

  public detached() {
    this.showReferralsNavItem?.dispose();
    this.paymentRequestEmailKeyUpdateSubscription?.off();
    this.routerNavigationSubscription?.off();
  }

  private activeRouteFragment(route: string): boolean {
    return router.activeInstruction().fragment.startsWith(route);
  }

  private updateCurrentTab() {
    // Figure out if the person is looking at the payment request
    this.checkMyInvoicesRoute();

    if (this.activeRouteFragment('user/payments')) {
      this.currentTab('payments');
    } else if (this.activeRouteFragment('user/requests')) {
      this.currentTab('paymentRequests');
    } else if (this.activeRouteFragment('user/payees')) {
      this.currentTab('payees');
    } else if (this.activeRouteFragment('user/bank')) {
      this.currentTab('bank');
    } else if (this.activeRouteFragment('user/approvals')) {
      this.currentTab('approvalRules');
    } else if (this.activeRouteFragment('user/documents')) {
      this.currentTab('documents');
    } else if (
      router.activeInstruction().fragment === 'user' ||
      this.activeRouteFragment('user/dashboard')
    ) {
      this.currentTab('dashboard');
    } else {
      this.currentTab(null);
    }
  }

  private NavigateHome = () => {
    // don't navigate anywhere when tutorial is visible
    if ($('body').hasClass('tutorial-visible')) {
      router.navigate('user/tutorials/companyInfo');
      return;
    }

    if (this.userService.IsAuthenticated() === false || !companyService.Instance.CompanyName()) {
      router.navigate('https://www.plooto.com/');
    } else {
      router.navigate('user');
    }
    window.scrollTo({ top: 0 });
  };

  private shouldShowGoHomeButton = ko.pureComputed<boolean>(
    () =>
      router.activeInstruction().fragment === 'user/settings/sso' ||
      router.activeInstruction().fragment === 'user/settings'
  );

  private LogoutUser = () => {
    segmentAnalytics.track(EventName.UserSignedOut, {
      source: FormEventType.ButtonClicked,
      context: SignoutContext.Settings,
    });
    segmentAnalytics.reset();

    this.userService.Logout();
  };

  private navigateSwitchCompany() {
    payablesStore.dispatch({ type: PayablesStoreActionType.Reset }); // reset company specific filters before changing the company
    receivablesStore.dispatch({ type: ReceivablesStoreActionType.Reset });
    contactsStore.dispatch({ type: ContactsStoreActionType.Reset });

    router.navigate('user/company/select');
  }

  private checkMyInvoicesRoute() {
    if (
      this.activeRouteFragment('requests/myInvoices/') ||
      this.activeRouteFragment('requests/display/')
    ) {
      const myInvoicesRoute = router
        .activeInstruction()
        .fragment.replace('requests/display/', '')
        .replace('requests/myInvoices/', '');
      const myInvoicesRouteSplit = myInvoicesRoute.split('/');

      this.myInvoicesUrl(`#requests/myInvoices/${myInvoicesRoute}`);
      this.paymentRequestEmailKey(myInvoicesRouteSplit[0]);
    } else {
      this.myInvoicesUrl(null);
    }
  }

  private reloadUnpaidInvoices() {
    emailPaymentRequestsService.Instance.countContactPaymentRequests(this.paymentRequestEmailKey())
      .then((unpaidContactPaymentRequests: number) => {
        this.myInvoicesCount(unpaidContactPaymentRequests);
      })
      .fail(() => {
        this.myInvoicesCount(null);
      });
  }

  private isNavigationButtonsVisible = ko.pureComputed({
    read: () => {
      // Hide navigation buttons if you are going through id verification
      if (
        this.activeRouteFragment('user/identityVerification') ||
        this.activeRouteFragment('user/company/select')
      ) {
        return false;
      }

      return true;
    },
    deferEvaluation: true,
  });

  private isAccountingFirm = ko.pureComputed({
    read: () => {
      if (companyService.Instance.profile()) {
        return companyService.Instance.profile().isAccountingFirm();
      }

      return false;
    },
    deferEvaluation: true,
  });

  private isSwitchCompanies = ko.pureComputed({
    read: () => {
      if (router.activeInstruction().fragment.includes('user/company/select')) {
        return true;
      }

      return false;
    },
  });

  private isSingleSignOnVisible = SingleSignOnFeatureToggleService.IsSingleSignOnEnabled;

  private navigateToReferrals() {
    CompanyEventService.emit(FormEventType.ButtonClicked, {
      context: 'dropDownMenu',
      buttonName: 'Referrals',
    });

    return true;
  }

  /**
   * Props to supply to NavbarTop, effectively bridging the business logic in this viewmodel with
   * the new presentation logic in React. Using a computed viewmodel field so we get type-safety
   * given the immense size of this props object.
   */
  public reactNavbarProps = ko.pureComputed({
    read: (): NavbarTopProps => ({
      navigateHome: this.NavigateHome,
      navigateSwitchCompany: this.navigateSwitchCompany,
      logoutUser: this.LogoutUser,
      settingsHref: this.settingsHref(),
      currentTab: this.currentTab(),
      shouldShowNavigation: Boolean(
        this.userService.IsAuthenticated() &&
          this.companyService.CompanyName() &&
          this.isNavigationButtonsVisible()
      ),
      shouldShowMyInvoices: Boolean(
        !(this.userService.IsAuthenticated() && this.companyService.CompanyName()) &&
          this.myInvoicesUrl()
      ),
      shouldShowHelpCenter: Boolean(
        this.userService.IsAuthenticated() &&
          ((this.companyService.CompanyName() && this.isNavigationButtonsVisible()) ||
            (!this.isSwitchCompanies() && this.userService.isSsoAdminUserWithoutNodePermissions()))
      ),
      shouldShowSwitchCompany: Boolean(
        this.userService.IsAuthenticated() &&
          this.companyService.CompanyName() &&
          this.isNavigationButtonsVisible()
      ),
      shouldShowReturnToDashboard: Boolean(
        this.userService.IsAuthenticated() &&
          (this.isSwitchCompanies() || this.userService.isSsoAdminUserWithoutNodePermissions()) &&
          !this.userService.profile().isSsoAdminUser() &&
          this.userService.memberships().length > 0
      ),
      shouldShowReturnToHome: Boolean(
        this.userService.IsAuthenticated() &&
          (this.isSwitchCompanies() || this.userService.isSsoAdminUserWithoutNodePermissions()) &&
          this.userService.profile().isSsoAdminUser() &&
          this.shouldShowGoHomeButton()
      ),
      shouldShowUserSettings: Boolean(
        this.userService.IsAuthenticated() &&
          ((this.companyService.CompanyName() && this.isNavigationButtonsVisible()) ||
            ((this.isSwitchCompanies() ||
              this.userService.isSsoAdminUserWithoutNodePermissions()) &&
              this.userService.profile().isSsoAdminUser()))
      ),
      shouldShowCompanySettings: Boolean(
        this.userService.IsAuthenticated() &&
          this.companyService.CompanyName() &&
          this.isNavigationButtonsVisible()
      ),
      shouldShowSsoSettings: Boolean(
        this.userService.IsAuthenticated() &&
          (this.isSwitchCompanies() || this.userService.isSsoAdminUserWithoutNodePermissions()) &&
          this.userService.profile().isSsoAdminUser()
      ),
      shouldShowReferrals: Boolean(this.showReferralsNavItem() && !this.isSwitchCompanies()),
      shouldShowLogOut: Boolean(this.userService.IsAuthenticated()),
      isSwitchCompanies: this.isSwitchCompanies(),
      myInvoicesUrl: this.myInvoicesUrl(),
      myInvoicesCount: this.myInvoicesCount(),
      navigateToReferrals: this.navigateToReferrals,
    }),
    deferEvaluation: true,
  });
}
